import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import numberDisplayI18n from "sipli-fleet-core/translationService";
import {
  Input,
  InputProps,
  Select,
  SelectProps,
  Space,
  TimePicker
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomSharedDatePicker, CustomShareRangePicker } from "sipli-fleet-core/components";
import { ALL_START_DATE } from "src/util/util.constant";
import { UtilService } from "src/util/util.service";
import useStartDateEndDatePickerFields from "../../Hooks/useStartDateEndDatePickerFields";
import { getDateFormatByLocal } from "../../utils/constants";
import { FIELDS_TYPE_CONSTANTS } from "./constant";

interface LabeledFieldProps {
  label?: string;
  labelClassName?: string;
  children: ReactNode;
}

const LabeledField = ({
  label,
  labelClassName,
  children,
}: LabeledFieldProps) => (
  <div className="min-w-[200px]">
    {label && (
      <label className={labelClassName || "block text-sm text-slate-600 mb-1"}>
        {label}
      </label>
    )}
    {children}
  </div>
);

export interface FilterConfig {
  type?: string;
  options?: SelectProps["options"];
  placeholder?: string;
  className?: string;
  key?: string;
  keys?: {
    start?: string;
    end?: string;
    startDate?: string;
    endDate?: string;
  };
  label?: string;
  labelClassName?: string;
  inputProps?: InputProps;
  mode?: "multiple" | "tags";
  maxTagCount?: number;
  countLabel?: string;
  useTimePicker?: boolean;
  defaultStartMinute?: number;
  defaultEndMinute?: number;
  disabledDateTodayOffsetDays?: number;
}

interface CustomFiltrationProps {
  handleFiltration?: (key: string, value: any) => void;
  handleApplyFilters?: () => void;
  clearFilters?: () => void;
  filters?: Record<string, FilterConfig> & {
    customFilterButtons?: ReactNode;
  };
  selectedFilters?: Record<string, any>;
  allStartDate?: any;
  filterContainerClassname?: string;
  searchBtnText?: string;
}

const CustomFiltration = ({
  handleFiltration = () => {},
  handleApplyFilters = () => {},
  clearFilters = () => {},
  filters = {},
  selectedFilters = {},
  allStartDate = ALL_START_DATE,
  filterContainerClassname = "",
  searchBtnText = "apply",
}: CustomFiltrationProps) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const dateFormat = getDateFormatByLocal(currentLang);

  const [error, setError] = useState<boolean | null>(null);

  const { handleRangePicker, onOpenChange } =
    useStartDateEndDatePickerFields();

  const handleChange = (key: string, value: any) => {
    handleFiltration(key, value);
  };

  const renderField = (confKey: string) => {
    const conf = filters[confKey] || {};

    const {
      type,
      options,
      placeholder,
      className,
      key: filterKey,
      keys: filterKeys,
      label,
      labelClassName,
      inputProps,
      mode,
      maxTagCount,
      countLabel,
      useTimePicker,
      defaultStartMinute,
      defaultEndMinute,
    } = conf;

    if (type === FIELDS_TYPE_CONSTANTS.Select) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <Select
            options={options}
            placeholder={placeholder}
            className={className}
            value={selectedFilters[filterKey as string] ?? null}
            onChange={(val) => handleChange(filterKey as string, val)}
            showSearch
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            mode={mode}
            maxTagCount={maxTagCount}
            maxTagPlaceholder={(omittedValues) => {
              const totalCount =
                (omittedValues?.length || 0) + (maxTagCount || 0);

              return `${formatNumber(totalCount, numberDisplayI18n.language)} ${countLabel || placeholder || ""}`;
            }}
          />
        </LabeledField>
      );
    }

    if (type === FIELDS_TYPE_CONSTANTS.Input) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <Input
            placeholder={placeholder}
            className={className}
            value={selectedFilters[filterKey as string] ?? ""}
            onChange={(e) =>
              handleChange(filterKey as string, e.target.value)
            }
            allowClear
            {...inputProps}
          />
        </LabeledField>
      );
    }

    if (type === FIELDS_TYPE_CONSTANTS.SearchInput) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <Input.Search
            placeholder={placeholder}
            className={className}
            value={selectedFilters[filterKey as string] ?? ""}
            onChange={(e) =>
              handleChange(filterKey as string, e.target.value)
            }
            onSearch={handleApplyFilters}
            allowClear
            {...inputProps}
          />
        </LabeledField>
      );
    }

    if (type === FIELDS_TYPE_CONSTANTS.Number) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <Input
            placeholder={placeholder}
            className={className}
            value={selectedFilters[filterKey as string] ?? ""}
            onChange={(e) => {
              const newValue = e.target.value;

              handleChange(filterKey as string, newValue);

              if (!isNaN(Number(newValue)) || newValue === "") {
                setError(false);
              } else {
                setError(true);
              }
            }}
            status={error ? "error" : ""}
            onKeyPress={(e) => {
              const char = String.fromCharCode(e.which);
              const regex = /^[0-9.]$/;

              if (!regex.test(char)) e.preventDefault();

              if (char === "." && e.currentTarget.value.includes(".")) {
                e.preventDefault();
              }
            }}
            allowClear
          />

          {error && (
            <div className="text-[red] mt-[5px]">
              Please enter a valid number
            </div>
          )}
        </LabeledField>
      );
    }

    if (type === FIELDS_TYPE_CONSTANTS.Date) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <CustomSharedDatePicker
            className={className}
            value={
              selectedFilters[filterKey as string]
                ? dayjs(selectedFilters[filterKey as string], dateFormat)
                : null
            }
            onChange={(date: Dayjs | null) => {
              const formatted = date?.isValid()
                ? date.format("YYYY-MM-DD")
                : null;

              handleChange(filterKey as string, formatted);
            }}
            allowClear
          />
        </LabeledField>
      );
    }

    // ✅ Fixed DateRange Picker
    if (type === FIELDS_TYPE_CONSTANTS.DateRange) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <CustomShareRangePicker
            value={
              selectedFilters[conf.keys?.startDate as string] &&
              selectedFilters[conf.keys?.endDate as string]
                ? [
                    dayjs(
                      selectedFilters[conf.keys?.startDate as string],
                      "YYYY-MM-DD",
                    ),
                    dayjs(
                      selectedFilters[conf.keys?.endDate as string],
                      "YYYY-MM-DD",
                    ),
                  ]
                : null
            }
            onChange={(dates: Dayjs[] | null) => {
              const [start, end] = dates
                ? dates.map((d) =>
                    d?.isValid() ? d.format("YYYY-MM-DD") : null,
                  )
                : [null, null];

              handleChange(conf.keys?.startDate as string, start);
              handleChange(conf.keys?.endDate as string, end);
            }}
            className={className}
            disabledDate={(current: Dayjs) => {
              // Default behavior in the app: block selecting up to "today" (keeps previous screens working)
              // Allow reports to override via filter config prop.

              const disabledDateTodayOffsetDays =
                conf?.disabledDateTodayOffsetDays ?? 2;

              const maxSelectableDate = dayjs()
                .subtract(disabledDateTodayOffsetDays, "day")
                .endOf("day");

              return UtilService.disabledDate(
                current,
                ALL_START_DATE,
                maxSelectableDate,
              );
            }}
            allowClear
          />
        </LabeledField>
      );
    }

    if (type === FIELDS_TYPE_CONSTANTS.DateRange2) {
      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <CustomRangePicker
            disabledDate={(current: Dayjs) =>
              UtilService.disabledDate(current, allStartDate)
            }
            onChange={(dates: Dayjs[], dateStrings: string[]) => {
              const values =
                UtilService.globalConvertDateValues(dateStrings);

              handleRangePicker(dates, values);

              handleChange(conf.key as string, [
                UtilService.dateRangePickerFormatedDate(values[0]),
                UtilService.dateRangePickerFormatedDate(
                  values[1],
                  "23:59:00",
                ),
              ]);
            }}
            onOpenChange={onOpenChange}
            className="py-2"
            allowClear
          />
        </LabeledField>
      );
    }

    if (type === FIELDS_TYPE_CONSTANTS.TimeRange) {
      // Default behavior: two Select dropdowns (start / end hour)

      if (!useTimePicker) {
        return (
          <LabeledField label={label} labelClassName={labelClassName}>
            <Space>
              <Select
                options={options}
                placeholder={t("start_time")}
                className={className}
                value={selectedFilters[filterKeys?.start as string] ?? null}
                onChange={(val) =>
                  handleChange(filterKeys?.start as string, val)
                }
              />

              <Select
                options={options}
                placeholder={t("end_time")}
                className={className}
                value={selectedFilters[filterKeys?.end as string] ?? null}
                onChange={(val) =>
                  handleChange(filterKeys?.end as string, val)
                }
              />
            </Space>
          </LabeledField>
        );
      }

      // Optional behavior: Time range picker (used when caller sets useTimePicker=true)

      const startHour = selectedFilters[filterKeys?.start as string] ?? 0;
      const endHour = selectedFilters[filterKeys?.end as string] ?? 23;

      const startMinute = defaultStartMinute ?? 0;
      const endMinute = defaultEndMinute ?? 0;

      const value =
        startHour !== null &&
        startHour !== undefined &&
        endHour !== null &&
        endHour !== undefined
          ? [
              dayjs().hour(startHour).minute(startMinute),
              dayjs().hour(endHour).minute(endMinute),
            ]
          : null;

      return (
        <LabeledField label={label} labelClassName={labelClassName}>
          <TimePicker.RangePicker
            format="HH:mm"
            className={className}
            value={value}
            onChange={(times) => {
              if (!times || times.length !== 2) {
                handleChange(filterKeys?.start as string, undefined);
                handleChange(filterKeys?.end as string, undefined);

                return;
              }

              const [start, end] = times;

              handleChange(
                filterKeys?.start as string,
                start?.hour(),
              );

              handleChange(
                filterKeys?.end as string,
                end?.hour(),
              );
            }}
          />
        </LabeledField>
      );
    }

    return <div className="h-[1px] w-full bg-transparent" />;
  };

  const hasSearchInput = Object.values(filters).some(
    (f) => f.type === FIELDS_TYPE_CONSTANTS.SearchInput,
  );

  return (
    <div className="w-full px-3 py-3">
      <div
        className={`flex justify-between items-center gap-3 ${filterContainerClassname}`}
      >
        <div className="flex flex-wrap items-start gap-4">
          {Object.keys(filters).map((k) => (
            <div key={k} className="min-w-[200px]">
              {renderField(k)}
            </div>
          ))}
        </div>

        {filters?.customFilterButtons ? (
          filters?.customFilterButtons
        ) : (
          <Space className="flex-shrink-0">
            {!hasSearchInput && (
              <CustomButton
                onClick={handleApplyFilters}
                type="primary"
                label={t(searchBtnText)}
              />
            )}

            <CustomButton
              onClick={clearFilters}
              type="secondary"
              label={t("reset")}
            />
          </Space>
        )}
      </div>
    </div>
  );
};

export default CustomFiltration;